import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPLabel, IPLink, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ModifyTextDialog from 'components/Dialogs/ModifyTextDialog';
import EntityPicker from 'components/Pickers/EntityPicker';
import BaseTableList from 'components/TableList/BaseTableList';
import SingleTask from 'components/Tasks/SingleTask';
import { getEntity } from 'globalFunctions';
import { deleteIcon, editIcon, globalStackTokensGapMedium } from 'globalStyles';
import Entity from 'models/entity';
import Task from 'models/tasks/task';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetTaskActionConfig {
  templateIds: [number, string][];

  constructor() {
    this.templateIds = [];
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.templateIds = newRawConfig.templateIds ?? [];
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTaskActionConfig {
    const newConfig = new WidgetTaskActionConfig();
    newConfig.templateIds = [...this.templateIds];

    return newConfig;
  }
}

interface IWidgetTaskActionProps extends IWidgetRendererProps {}

const WidgetTaskAction: FunctionComponent<IWidgetTaskActionProps> = (props: IWidgetTaskActionProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [templates, setTemplates] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.templates.getItems();
        const config = loadConfig();

        //get templates specified in config from cache
        const ids = config.templateIds?.map((id) => id[0]) ?? [];
        const templates = appContext.globalDataCache.templates.getItemsForId(ids).map((t) => t.clone());
        //update the names
        templates.forEach((t) => {
          const templateConfig = config.templateIds.find((id) => id[0] === t.taskId);
          if (templateConfig) {
            t.name = templateConfig[1];
          }
        });
        setTemplates(templates);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTaskActionConfig => {
    const newConfig = new WidgetTaskActionConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  //
  // Render table
  //
  const defColumns: IColumn[] = [
    {
      key: 'template',
      name: t('widgets:TaskAction.Template'),
      minWidth: 200,
      maxWidth: 500,
      isMultiline: true,
      isResizable: true,
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>(defColumns);

  const renderItem = (item: Task, column: IColumn): JSX.Element | null => {
    switch (column.key) {
      case 'template':
        return renderTemplate(item);
      default:
        return null;
    }
  };

  const renderTemplate = (item: Task): JSX.Element | null => {
    return (
      <IPText>
        <IPLink onClick={() => startNewTask(item)}>{item.name}</IPLink>
      </IPText>
    );
  };

  const startNewTask = (item: Task) => {
    let newTask = new Task();
    newTask = newTask.applyTemplate(item, appContext);
    //apply the orginal name of the template from global cache
    newTask.name = appContext.globalDataCache.templates.get(item.taskId)?.name ?? item.name;

    setSelectedTask(newTask);
  };

  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    setSelectedTask(undefined);
  };

  const removeTask = async (task: Task) => {
    setSelectedTask(undefined);
  };

  const updateTask = async (task: Task) => {};

  //
  // Main render
  //
  if (!templates || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (templates.length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:TaskAction.Empty')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill>
      <IPStack.Item grow>
        <BaseTableList
          items={templates}
          columns={columns}
          isLoading={isLoading}
          setColumns={setColumns}
          onRenderCol={renderItem}
          compact={true}
          disableSort={true}
          hideHeader={true}
        />
        {selectedTask && (
          <SingleTask
            task={selectedTask}
            isOpen={true}
            close={() => {
              setSelectedTask(undefined);
            }}
            onSave={saveTask}
            onRemove={removeTask}
            onUpdate={updateTask}
          />
        )}
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTaskAction;

//
// Config
//

interface IWidgetConfigWidgetTaskActionProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTaskAction: FunctionComponent<IWidgetConfigWidgetTaskActionProps> = (
  props: IWidgetConfigWidgetTaskActionProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [templates, setTemplates] = useState<Task[]>([]);
  const [showEditName, setShowEditName] = useState<Task | undefined>(undefined);
  const [newName, setNewName] = useState<string>('');
  const maxActions = 8; //maximum number of actions allowed
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTaskActionConfig => {
    let newRawConfig = new WidgetTaskActionConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.templates.getItems();
      const config = loadConfig();

      const ids = config.templateIds?.map((id) => id[0]) ?? [];
      const templates = appContext.globalDataCache.templates.getItemsForId(ids).map((t) => t.clone());
      //update the names
      templates.forEach((t) => {
        const templateConfig = config.templateIds.find((id) => id[0] === t.taskId);
        if (templateConfig) {
          t.name = templateConfig[1];
        }
      });
      setTemplates(templates);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (templates: Task[]): WidgetTaskActionConfig => {
    const config = new WidgetTaskActionConfig();
    config.templateIds = [];
    templates.forEach((t) => {
      config.templateIds.push([t.taskId, t.name]);
    });

    return config;
  };

  //
  // Render table
  //
  const defColumns: IColumn[] = [
    {
      key: 'template',
      name: t('widgets:TaskAction.Config.Template'),
      minWidth: 200,
      maxWidth: 500,
      isMultiline: true,
      isResizable: true,
    },
    {
      key: 'edit',
      name: '',
      minWidth: 20,
      maxWidth: 20,
    },
    {
      key: 'delete',
      name: '',
      minWidth: 20,
      maxWidth: 20,
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>(defColumns);

  const renderItem = (item: Task, column: IColumn): JSX.Element | null => {
    switch (column.key) {
      case 'template':
        return renderTemplate(item);
      case 'edit':
        return renderEdit(item);
      case 'delete':
        return renderDelete(item);
      default:
        return null;
    }
  };

  const renderEdit = (item: Task): JSX.Element | null => {
    return (
      <IPIconButton
        disabled={isLoading}
        iconProps={editIcon}
        onClick={() => {
          if (!item) return;
          setNewName(item.name);
          setShowEditName(item);
        }}
        styles={{ root: { height: 18 } }}
      />
    );
  };

  const renderDelete = (item: Task): JSX.Element | null => {
    return (
      <IPIconButton
        disabled={isLoading}
        iconProps={deleteIcon}
        onClick={() => {
          if (!item) return;
          onRemoveSelectedEntity(item);
        }}
        styles={{ root: { height: 18 } }}
      />
    );
  };

  const renderTemplate = (item: Task): JSX.Element | null => {
    return <IPText>{item.name}</IPText>;
  };

  const onAddSelectedEntity = (entity: Entity) => {
    //update state with new template
    const newTemplates = [...templates];
    const template = appContext.globalDataCache.templates.get(entity.entityId);
    newTemplates.push(template);
    setTemplates(newTemplates);
    //update config
    const config = getConfig(newTemplates);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemoveSelectedEntity = (task: Task) => {
    //update state with removed template
    const newTemplates = templates.filter((t) => t.taskId !== task.taskId);
    setTemplates(newTemplates);
    //update config
    const config = getConfig(newTemplates);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onEditSelectedEntity = (task: Task, newName: string | undefined) => {
    if (!newName || newName.length === 0) return;
    //update state with new name
    const newTemplates = templates.map((t) => {
      if (t.taskId === task.taskId) {
        t.name = newName;
      }

      return t;
    });
    setTemplates(newTemplates);
    //update config
    const config = getConfig(templates);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const getEntities = (): Entity[] => {
    const allEntities = appContext.globalDataCache.templates.items.map((t) => getEntity(t));

    return allEntities.filter((e) => {
      return !templates.some((t) => t.taskId === e.entityId);
    });
  };

  // Drag and drop handler for reordering templates
  const onDrop = (draggedItem: Task, targetItem: Task, draggedIndex: number, targetIndex: number) => {
    if (draggedIndex === targetIndex) return;
    const newTemplates = [...templates];
    // Remove dragged item
    newTemplates.splice(draggedIndex, 1);
    // Insert at new position
    newTemplates.splice(targetIndex, 0, draggedItem);
    setTemplates(newTemplates);
    // Update config
    const config = getConfig(newTemplates);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        {templates.length === 0 && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPText>{t('widgets:TaskAction.Config.Empty')}</IPText>
          </IPStack>
        )}
        {templates.length > 0 && (
          <BaseTableList
            items={templates}
            columns={columns}
            isLoading={isLoading}
            setColumns={setColumns}
            onRenderCol={renderItem}
            compact={true}
            disableSort={true}
            disableEmptyState={true}
            onDrop={onDrop}
            enableRowDragDrop={true}
          />
        )}
        {showEditName && (
          <ModifyTextDialog
            title={t('widgets:TaskAction.Config.NewName')}
            value={newName}
            isOpen={showEditName !== undefined}
            onClose={() => setShowEditName(undefined)}
            onUpdate={(value) => setNewName(value)}
            onSave={(value) => onEditSelectedEntity(showEditName, value)}
            required={true}
            maxLength={64}
          />
        )}
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:TaskAction.Config.TemplatePickerLabel')}</IPLabel>
        {templates.length < maxActions && (
          <EntityPicker
            entities={getEntities()}
            isLoading={isLoading}
            addSelectedEntity={onAddSelectedEntity}
            loadData={() => {}}
            isOnPanel={true}
          />
        )}
      </IPStack.Item>
    </IPStack>
  );
};
